import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import {
  CheckIcon,
  ArrowRightIcon,
  ServerIcon,
  UsersIcon,
  StoreIcon,
  Sparkles
} from 'lucide-react';
import { useTheme } from '../hooks/useTheme';

if (typeof window !== 'undefined') {
  gsap.registerPlugin(ScrollTrigger);
}

interface UserTypeCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  features: string[];
  ctaLabel: string;
  accentColor: string;
  index: number;
}

function UserTypeCard({
  icon,
  title,
  description,
  features,
  ctaLabel,
  accentColor,
  index
}: UserTypeCardProps) {
  const { isDark } = useTheme();
  const cardRef = useRef<HTMLDivElement>(null);
  const glowRef = useRef<HTMLDivElement>(null);
  const iconRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!cardRef.current) return;

    gsap.fromTo(
      cardRef.current,
      { opacity: 0, y: 40, scale: 0.95 },
      {
        opacity: 1,
        y: 0,
        scale: 1,
        duration: 0.6,
        ease: 'power2.out',
        scrollTrigger: {
          trigger: cardRef.current,
          start: 'top bottom-=80',
          toggleActions: 'play none none none',
          once: true
        },
        delay: index * 0.15
      }
    );
  }, [index]);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!glowRef.current || !cardRef.current) return;

    const rect = cardRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;

    glowRef.current.style.background = `radial-gradient(circle at ${x}% ${y}%, rgba(6, 182, 212, 0.12), transparent 60%)`;
  };

  const handleMouseEnter = () => {
    if (iconRef.current) {
      gsap.to(iconRef.current, {
        scale: 1.1,
        rotate: 8,
        duration: 0.4,
        ease: 'back.out(1.5)'
      });
    }
  };

  const handleMouseLeave = () => {
    if (iconRef.current) {
      gsap.to(iconRef.current, {
        scale: 1,
        rotate: 0,
        duration: 0.4,
        ease: 'back.out(1.5)'
      });
    }
  };

  return (
    <motion.div
       className="relative group h-full"
       whileHover={{ y: -20 }}
       transition={{ type: 'spring', stiffness: 120, damping: 20 }} 
    >
      {/* Outer cyan glow */}
      <div className="absolute -inset-[2px] rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-gradient-to-br from-cyan-500/40 via-cyan-400/30 to-transparent"
           style={{ filter: 'blur(12px)' }} />

      <div
        ref={cardRef}
        onMouseMove={handleMouseMove}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className={`relative rounded-2xl p-8 h-full flex flex-col border-2 transition-all duration-500 ${
          isDark
            ? 'bg-gradient-to-br from-slate-900/95 to-slate-800/95 border-slate-700/50 group-hover:border-cyan-500/60 shadow-xl'
            : 'bg-gradient-to-br from-white to-slate-50/80 border-slate-200/60 group-hover:border-cyan-400/80 shadow-xl'
        }`}
        style={{ backdropFilter: 'blur(8px)' }}
      >
        {/* Dynamic radial glow */}
        <div
          ref={glowRef}
          className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
        />

        {/* Top gradient line */}
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-500/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

        {/* Icon */}
        <div
          ref={iconRef}
          className={`relative inline-flex p-5 rounded-xl mb-6 w-fit transition-all duration-500 ${
            isDark
              ? 'bg-cyan-500/10 group-hover:bg-cyan-500/20 shadow-lg shadow-cyan-500/20'
              : 'bg-gradient-to-br from-cyan-100 to-blue-100 group-hover:from-cyan-200 group-hover:to-blue-200 shadow-lg shadow-cyan-500/10'
          }`}
        >
          {icon}
          {/* Icon glow */}
          <div className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-cyan-500/20"
               style={{ filter: 'blur(8px)' }} />
        </div>

        {/* Title */}
        <h3 className={`text-2xl md:text-3xl font-bold mb-4 tracking-tight transition-colors duration-300 ${
          isDark ? 'text-white group-hover:text-cyan-400' : 'text-slate-900 group-hover:text-cyan-600'
        }`}>
          {title}
        </h3>

        {/* Description */}
        <p className={`mb-6 leading-relaxed text-sm md:text-base ${
          isDark ? 'text-slate-400' : 'text-slate-600'
        }`}>
          {description}
        </p>

        {/* Features List */}
        <ul className="space-y-3.5 mb-8 flex-1">
          {features.map((feature, i) => (
            <li key={i} className="flex items-start gap-3 group/item">
              <div className={`flex-shrink-0 mt-0.5 p-1.5 rounded-lg transition-all duration-300 ${
                isDark
                  ? 'bg-cyan-500/10 group-hover/item:bg-cyan-500/20'
                  : 'bg-cyan-100 group-hover/item:bg-cyan-200'
              }`}>
                <CheckIcon className={`w-3.5 h-3.5 ${isDark ? 'text-cyan-400' : 'text-cyan-600'}`} />
              </div>
              <span className={`text-sm md:text-base ${
                isDark ? 'text-slate-300' : 'text-slate-700'
              }`}>
                {feature}
              </span>
            </li>
          ))}
        </ul>

        {/* CTA Button */}
        <a
          href="#pricing"
          className={`group/btn relative inline-flex items-center justify-center gap-2 px-6 py-4 rounded-xl text-sm font-bold border-2 overflow-hidden transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] ${
            isDark
              ? 'bg-gradient-to-r from-cyan-500/10 to-blue-500/10 text-cyan-400 border-cyan-500/30 hover:border-cyan-500/60'
              : 'bg-gradient-to-r from-cyan-50 to-blue-50 text-cyan-700 border-cyan-300 hover:border-cyan-500'
          }`}
        >
          <span className="relative z-10">{ctaLabel}</span>
          <ArrowRightIcon className="w-4 h-4 relative z-10 group-hover/btn:translate-x-1 transition-transform duration-300" />
          
          {/* Button glow */}
          <div className={`absolute inset-0 rounded-xl opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300 ${
            isDark ? 'bg-cyan-500/10' : 'bg-cyan-400/10'
          }`} />
        </a>

        {/* Bottom gradient line */}
        <div className="absolute bottom-0 left-0 right-0 h-1 rounded-b-2xl overflow-hidden">
          <div className={`h-full transform -translate-x-full group-hover:translate-x-0 transition-transform duration-700 ease-out ${
            isDark
              ? 'bg-gradient-to-r from-cyan-500 via-blue-500 to-violet-500'
              : 'bg-gradient-to-r from-cyan-400 via-blue-400 to-violet-400'
          }`} />
        </div>
      </div>
    </motion.div>
  );
}

export function BusinessModel() {
  const { isDark } = useTheme();
  const headerRef = useRef<HTMLDivElement>(null);
  const badgeRef = useRef<HTMLSpanElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const descRef = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    if (!headerRef.current) return;

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: headerRef.current,
        start: 'top 80%',
        toggleActions: 'play none none none',
        once: true
      }
    });

    tl.fromTo(
      badgeRef.current,
      { opacity: 0, y: -15, scale: 0.9 },
      { opacity: 1, y: 0, scale: 1, duration: 0.5, ease: 'back.out(1.5)' }
    )
    .fromTo(
      titleRef.current,
      { opacity: 0, y: 25 },
      { opacity: 1, y: 0, duration: 0.6, ease: 'power2.out' },
      '-=0.3'
    )
    .fromTo(
      descRef.current,
      { opacity: 0, y: 15 },
      { opacity: 1, y: 0, duration: 0.5, ease: 'power2.out' },
      '-=0.4'
    );
  }, []);

  const businessModels = [
    {
      icon: <ServerIcon className={`w-8 h-8 ${isDark ? 'text-cyan-400' : 'text-cyan-600'}`} />,
      title: 'Engines',
      description: 'Payment engines and platforms that need white-label infrastructure to power their ecosystem. Full API access with custom branding.',
      features: [
        'White-label payment processing',
        'Multi-tenant architecture',
        'Custom settlement schedules',
        'Dedicated infrastructure',
        'Volume-based pricing',
        'Priority engineering support'
      ],
      ctaLabel: 'Explore Engines',
      accentColor: 'bg-cyan-500'
    },
    {
      icon: <UsersIcon className={`w-8 h-8 ${isDark ? 'text-violet-400' : 'text-violet-600'}`} />,
      title: 'Resellers',
      description: 'Partners and resellers who want to offer payment services under their own brand. Earn revenue while we handle the complexity.',
      features: [
        'Revenue sharing model',
        'Branded merchant onboarding',
        'Partner dashboard & analytics',
        'Sub-merchant management',
        'Automated commission payouts',
        'Co-marketing support'
      ],
      ctaLabel: 'Become a Partner',
      accentColor: 'bg-violet-500'
    },
    {
      icon: <StoreIcon className={`w-8 h-8 ${isDark ? 'text-indigo-400' : 'text-indigo-600'}`} />,
      title: 'Merchants',
      description: 'Direct businesses of all sizes that need reliable, fast payment processing. From startups to enterprise — we scale with you.',
      features: [
        'Quick 5-minute integration',
        'Pre-built checkout components',
        'Real-time transaction monitoring',
        'Instant settlement options',
        'Multi-currency acceptance',
        'Built-in fraud protection'
      ],
      ctaLabel: 'Start Accepting',
      accentColor: 'bg-indigo-500'
    }
  ];

  return (
    <section
      id="solutions"
      className="relative py-24 lg:py-32 px-4 sm:px-6 lg:px-8 overflow-hidden"
    >
      <div className="max-w-7xl mx-auto relative z-10">
        {/* Header */}
        <div ref={headerRef} className="text-center mb-16 lg:mb-20">
          <span
            ref={badgeRef}
            className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-bold tracking-wide uppercase mb-8 border-2 ${
              isDark
                ? 'bg-cyan-500/10 border-cyan-500/30 text-cyan-400'
                : 'bg-cyan-100 border-cyan-300 text-cyan-700'
            }`}
          >
            <Sparkles className="w-4 h-4" />
            Solutions
          </span>

          <h2
            ref={titleRef}
            className={`text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-6 ${
              isDark ? 'text-white' : 'text-slate-900'
            }`}
          >
            Built for{' '}
            <span className={`relative inline-block ${
              isDark
                ? 'text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-blue-400 to-violet-400'
                : 'text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 via-blue-600 to-violet-600'
            }`}>
              every business
            </span>
          </h2>

          <p
            ref={descRef}
            className={`text-lg md:text-xl max-w-3xl mx-auto leading-relaxed ${
              isDark ? 'text-slate-400' : 'text-slate-600'
            }`}
          >
            Whether you're a platform, reseller, or merchant — 1401 adapts to your business
            model with{' '}
            <span className={`font-bold ${isDark ? 'text-cyan-400' : 'text-cyan-600'}`}>
              tailored solutions
            </span>
            .
          </p>
        </div>

        {/* Business Model Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-10">
          {businessModels.map((model, idx) => (
            <UserTypeCard
              key={idx}
              icon={model.icon}
              title={model.title}
              description={model.description}
              features={model.features}
              ctaLabel={model.ctaLabel}
              accentColor={model.accentColor}
              index={idx}
            />
          ))}
        </div>
      </div>
    </section>
  );
}